/**
 * NewResultsView — v2 results layout
 *
 * Feature-flagged alternative to the inline results block in SgpFlowScreen.
 * Keeps all existing UnifiedBetCard logic; redesigns the surrounding layout:
 *   Desktop (≥1024px) — sticky left sidebar (260px) + scrollable right content
 *   Mobile/Tablet     — "Filters" button + slide-in drawer
 *
 * Props are intentionally coarse-grained: pass the same state & handlers
 * that already exist in SgpFlowScreen — no new state or logic is introduced here.
 */

import React, { useState, useCallback } from 'react';
import {
  View,
  Text,
  TextInput,
  TouchableOpacity,
  ScrollView,
  Modal,
  StyleSheet,
  Platform,
  Dimensions,
  ActivityIndicator,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import BetCardV2 from './BetCardV2';
import PipelineDebugPanel from './PipelineDebugPanel';
import { MAJOR_SPORTSBOOKS, SECONDARY_SPORTSBOOKS, INTERNATIONAL_SPORTSBOOKS, DFS_SPORTSBOOKS } from '../../utils/sportsbookTiers';
import { describeResultsEmpty } from '../utils/newResultsEmptyState';

const { width: SCREEN_WIDTH } = Dimensions.get('window');
const IS_DESKTOP = SCREEN_WIDTH >= 1024;
const IS_TABLET = SCREEN_WIDTH >= 768 && SCREEN_WIDTH < 1024;

// ─── Tiny helpers ────────────────────────────────────────────────────────────

function Chip({ label, active, onPress, star }) {
  return (
    <TouchableOpacity
      onPress={onPress}
      activeOpacity={0.75}
      style={[chipStyles.chip, active && chipStyles.chipActive]}
    >
      {star && <Ionicons name="star" size={10} color="#fbbf24" style={{ marginRight: 3 }} />}
      <Text style={[chipStyles.chipText, active && chipStyles.chipTextActive]} numberOfLines={1}>
        {label}
      </Text>
    </TouchableOpacity>
  );
}

function AccordionSection({ title, icon, defaultOpen = false, children, badge }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <View style={accordionStyles.section}>
      <TouchableOpacity
        onPress={() => setOpen(o => !o)}
        activeOpacity={0.8}
        style={accordionStyles.header}
      >
        <View style={{ flexDirection: 'row', alignItems: 'center', gap: 6, flex: 1 }}>
          {icon && <Ionicons name={icon} size={13} color="rgba(255,255,255,0.5)" />}
          <Text style={accordionStyles.headerText}>{title}</Text>
          {badge > 0 && (
            <View style={accordionStyles.badge}>
              <Text style={accordionStyles.badgeText}>{badge}</Text>
            </View>
          )}
        </View>
        <Ionicons
          name={open ? 'chevron-up' : 'chevron-down'}
          size={13}
          color="rgba(255,255,255,0.4)"
        />
      </TouchableOpacity>
      {open && <View style={accordionStyles.body}>{children}</View>}
    </View>
  );
}

function PaginationRow({ currentPage, totalPages, onPageChange }) {
  if (totalPages <= 1) return null;
  const pages = buildPageNumbers(currentPage, totalPages);
  return (
    <View style={pgStyles.row}>
      <TouchableOpacity
        onPress={() => onPageChange(currentPage - 1)}
        disabled={currentPage <= 1}
        style={[pgStyles.btn, currentPage <= 1 && pgStyles.btnDisabled]}
      >
        <Ionicons name="chevron-back" size={14} color={currentPage <= 1 ? 'rgba(255,255,255,0.2)' : '#fff'} />
      </TouchableOpacity>
      {pages.map((p, i) =>
        p === '…' ? (
          <Text key={`ellipsis-${i}`} style={pgStyles.ellipsis}>…</Text>
        ) : (
          <TouchableOpacity
            key={p}
            onPress={() => onPageChange(p)}
            style={[pgStyles.btn, p === currentPage && pgStyles.btnActive]}
          >
            <Text style={[pgStyles.btnText, p === currentPage && pgStyles.btnTextActive]}>{p}</Text>
          </TouchableOpacity>
        )
      )}
      <TouchableOpacity
        onPress={() => onPageChange(currentPage + 1)}
        disabled={currentPage >= totalPages}
        style={[pgStyles.btn, currentPage >= totalPages && pgStyles.btnDisabled]}
      >
        <Ionicons name="chevron-forward" size={14} color={currentPage >= totalPages ? 'rgba(255,255,255,0.2)' : '#fff'} />
      </TouchableOpacity>
    </View>
  );
}

function buildPageNumbers(current, total) {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
  const pages = new Set([1, total]);
  for (let p = Math.max(1, current - 2); p <= Math.min(total, current + 2); p++) pages.add(p);
  const sorted = Array.from(pages).sort((a, b) => a - b);
  const result = [];
  let prev = 0;
  for (const p of sorted) {
    if (p - prev > 1) result.push('…');
    result.push(p);
    prev = p;
  }
  return result;
}

// ─── Sort options ─────────────────────────────────────────────────────────────

const SORT_OPTIONS = [
  { value: 'ev', label: 'Best EV' },
  { value: 'odds', label: 'Best Odds' },
  { value: 'price_asc', label: 'Lowest Odds' },
  { value: 'date', label: 'By Date' },
  { value: 'sportsbooks', label: 'Most Books' },
  { value: 'market', label: 'Market A–Z' },
];

function orderSportsbooksByTier(books) {
  if (!Array.isArray(books) || books.length === 0) return [];
  const present = new Set(books);
  const tierOrder = [
    ...MAJOR_SPORTSBOOKS,
    ...SECONDARY_SPORTSBOOKS,
    ...INTERNATIONAL_SPORTSBOOKS,
    ...DFS_SPORTSBOOKS,
  ];
  const inTier = tierOrder.filter((b) => present.has(b));
  const rest = books.filter((b) => !tierOrder.includes(b));
  return [...inTier, ...rest];
}

// ─── Filter Sidebar content (shared between sidebar and drawer) ────────────────

function FilterContent({
  searchText, setSearchText,
  filterSportsbooks, setFilterSportsbooks,
  filterMarkets, setFilterMarkets,
  filterPlayers, setFilterPlayers,
  filterResultType, setFilterResultType,
  filterDeepLinksOnly, setFilterDeepLinksOnly,
  filterPositiveEvOnly, setFilterPositiveEvOnly,
  filterLineType, setFilterLineType,
  filterLegCount, setFilterLegCount,
  sortBy, setSortBy,
  availableSportsbooks, availableMarkets, availablePlayers,
  myBooks,
  totalFilteredCount, totalCount,
  isLiveQuery,
  onClearAll,
  hasActiveFilters,
  availableLegCounts,
  _effectiveBooksSort,
  setBooksViewOverride,
  _allBooksCount,
  _myBooksCount,
  lineTypeCounts,
}) {
  const toggleBook = (book) => {
    setFilterSportsbooks(prev =>
      prev.includes(book) ? prev.filter(b => b !== book) : [...prev, book]
    );
  };
  const toggleMarket = (market) => {
    setFilterMarkets(prev =>
      prev.includes(market) ? prev.filter(m => m !== market) : [...prev, market]
    );
  };
  const togglePlayer = (player) => {
    setFilterPlayers(prev =>
      prev.includes(player) ? prev.filter(p => p !== player) : [...prev, player]
    );
  };

  return (
    <View style={filterContentStyles.root}>
      {/* Header */}
      <View style={filterContentStyles.header}>
        <View style={{ flexDirection: 'row', alignItems: 'center', gap: 6 }}>
          <Ionicons name="filter" size={14} color="rgba(255,255,255,0.6)" />
          <Text style={filterContentStyles.headerTitle}>Filters</Text>
          {hasActiveFilters && (
            <View style={filterContentStyles.activeBadge}>
              <Text style={filterContentStyles.activeBadgeText}>ON</Text>
            </View>
          )}
        </View>
        <View style={{ flexDirection: 'row', alignItems: 'center', gap: 4 }}>
          <Text style={filterContentStyles.countText}>
            {totalFilteredCount}<Text style={{ color: 'rgba(255,255,255,0.35)' }}>/{totalCount}</Text>
          </Text>
          {isLiveQuery && (
            <View style={filterContentStyles.livePill}>
              <View style={filterContentStyles.liveDot} />
              <Text style={filterContentStyles.liveText}>LIVE</Text>
            </View>
          )}
        </View>
      </View>

      {/* Search */}
      <View style={filterContentStyles.searchRow}>
        <Ionicons name="search" size={14} color="rgba(255,255,255,0.4)" style={{ marginRight: 6 }} />
        <TextInput
          style={filterContentStyles.searchInput}
          value={searchText}
          onChangeText={setSearchText}
          placeholder="Team, player, market…"
          placeholderTextColor="rgba(255,255,255,0.3)"
        />
        {!!searchText && (
          <TouchableOpacity onPress={() => setSearchText('')} hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}>
            <Ionicons name="close-circle" size={14} color="rgba(255,255,255,0.4)" />
          </TouchableOpacity>
        )}
      </View>

      {/* Clear all */}
      {hasActiveFilters && (
        <TouchableOpacity onPress={onClearAll} style={filterContentStyles.clearBtn} activeOpacity={0.75}>
          <Ionicons name="refresh" size={12} color="#A259FF" />
          <Text style={filterContentStyles.clearBtnText}>Clear all filters</Text>
        </TouchableOpacity>
      )}

      {/* Sort */}
      <AccordionSection title="Sort" icon="swap-vertical-outline" defaultOpen badge={sortBy !== 'ev' ? 1 : 0}>
        <View style={{ gap: 2 }}>
          {SORT_OPTIONS.map(opt => (
            <TouchableOpacity
              key={opt.value}
              onPress={() => setSortBy(opt.value)}
              activeOpacity={0.8}
              style={[filterContentStyles.sortRow, sortBy === opt.value && filterContentStyles.sortRowActive]}
            >
              {sortBy === opt.value && (
                <Ionicons name="checkmark" size={12} color="#A259FF" style={{ marginRight: 4 }} />
              )}
              <Text style={[filterContentStyles.sortLabel, sortBy === opt.value && filterContentStyles.sortLabelActive]}>
                {opt.label}
              </Text>
            </TouchableOpacity>
          ))}
        </View>
      </AccordionSection>

      {typeof setBooksViewOverride === 'function' && (myBooks?.length > 0) && (
        <AccordionSection
          title="Books mode"
          icon="star-outline"
          defaultOpen
          badge={_effectiveBooksSort === 'mybooks' ? 1 : 0}
        >
          <View style={{ flexDirection: 'row', gap: 6, flexWrap: 'wrap' }}>
            <Chip
              label={`All Books (${_allBooksCount ?? 0})`}
              active={_effectiveBooksSort !== 'mybooks'}
              onPress={() => setBooksViewOverride('ev')}
            />
            <Chip
              label={`My Books (${_myBooksCount ?? 0})`}
              active={_effectiveBooksSort === 'mybooks'}
              onPress={() => setBooksViewOverride('mybooks')}
              star
            />
          </View>
        </AccordionSection>
      )}

      {/* Sportsbooks */}
      {availableSportsbooks.length > 0 && (
        <AccordionSection
          title="Sportsbook"
          icon="bookmark-outline"
          defaultOpen={filterSportsbooks.length > 0}
          badge={filterSportsbooks.length}
        >
          <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 6 }}>
            {orderSportsbooksByTier(availableSportsbooks).map(book => {
              const isMine = myBooks?.some(mb => mb?.toLowerCase() === book?.toLowerCase());
              return (
                <Chip
                  key={book}
                  label={book}
                  active={filterSportsbooks.includes(book)}
                  onPress={() => toggleBook(book)}
                  star={isMine}
                />
              );
            })}
          </View>
        </AccordionSection>
      )}

      {/* Markets */}
      {availableMarkets.length > 0 && (
        <AccordionSection
          title="Market"
          icon="pricetag-outline"
          defaultOpen={filterMarkets.length > 0}
          badge={filterMarkets.length}
        >
          <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 6 }}>
            {availableMarkets.map(market => (
              <Chip
                key={market}
                label={market}
                active={filterMarkets.includes(market)}
                onPress={() => toggleMarket(market)}
              />
            ))}
          </View>
        </AccordionSection>
      )}

      {/* Players */}
      {availablePlayers?.length > 0 && (
        <AccordionSection
          title="Player"
          icon="person-outline"
          defaultOpen={filterPlayers.length > 0}
          badge={filterPlayers.length}
        >
          <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 6 }}>
            {availablePlayers.map(p => (
              <Chip
                key={p.name}
                label={p.name}
                active={filterPlayers.includes(p.name)}
                onPress={() => togglePlayer(p.name)}
              />
            ))}
          </View>
        </AccordionSection>
      )}

      {/* Result type */}
      <AccordionSection title="Result type" icon="layers-outline" defaultOpen={false} badge={filterResultType !== 'all' ? 1 : 0}>
        <View style={{ flexDirection: 'row', gap: 6, flexWrap: 'wrap' }}>
          {[{ v: 'all', l: 'All' }, { v: 'team', l: 'Team Odds' }, { v: 'props', l: 'Player Props' }].map(({ v, l }) => (
            <Chip key={v} label={l} active={filterResultType === v} onPress={() => setFilterResultType(v)} />
          ))}
        </View>
      </AccordionSection>

      {/* Lines */}
      <AccordionSection title="Lines" icon="git-branch-outline" defaultOpen={false} badge={filterLineType !== 'all' ? 1 : 0}>
        <View style={{ flexDirection: 'row', gap: 6 }}>
          {[
            { v: 'all', l: 'All', n: lineTypeCounts?.all },
            { v: 'main', l: 'Main', n: lineTypeCounts?.main },
            { v: 'alt', l: 'Alt', n: lineTypeCounts?.alt },
          ].map(({ v, l, n }) => (
            <Chip
              key={v}
              label={Number.isFinite(n) ? `${l} (${n})` : l}
              active={filterLineType === v}
              onPress={() => setFilterLineType(v)}
            />
          ))}
        </View>
      </AccordionSection>

      {Array.isArray(availableLegCounts) && availableLegCounts.length > 0 && (
        <AccordionSection
          title="Parlay size"
          icon="git-network-outline"
          defaultOpen={filterLegCount != null}
          badge={filterLegCount != null ? 1 : 0}
        >
          <View style={{ flexDirection: 'row', gap: 6, flexWrap: 'wrap' }}>
            <Chip label="All" active={filterLegCount == null} onPress={() => setFilterLegCount(null)} />
            {availableLegCounts.map((lc) => (
              <Chip
                key={lc}
                label={`${lc}-Leg`}
                active={filterLegCount === lc}
                onPress={() => setFilterLegCount(filterLegCount === lc ? null : lc)}
              />
            ))}
          </View>
        </AccordionSection>
      )}

      {/* Quick toggles */}
      <AccordionSection title="Quick filters" icon="flash-outline" defaultOpen>
        <View style={{ gap: 8 }}>
          <TouchableOpacity
            onPress={() => setFilterPositiveEvOnly(!filterPositiveEvOnly)}
            activeOpacity={0.8}
            style={filterContentStyles.toggleRow}
          >
            <View style={[filterContentStyles.toggleTrack, filterPositiveEvOnly && filterContentStyles.toggleTrackOn]}>
              <View style={[filterContentStyles.toggleThumb, filterPositiveEvOnly && filterContentStyles.toggleThumbOn]} />
            </View>
            <Text style={filterContentStyles.toggleLabel}>+EV only</Text>
          </TouchableOpacity>
          <TouchableOpacity
            onPress={() => setFilterDeepLinksOnly(!filterDeepLinksOnly)}
            activeOpacity={0.8}
            style={filterContentStyles.toggleRow}
          >
            <View style={[filterContentStyles.toggleTrack, filterDeepLinksOnly && filterContentStyles.toggleTrackOn]}>
              <View style={[filterContentStyles.toggleThumb, filterDeepLinksOnly && filterContentStyles.toggleThumbOn]} />
            </View>
            <Text style={filterContentStyles.toggleLabel}>Direct links only</Text>
          </TouchableOpacity>
        </View>
      </AccordionSection>
    </View>
  );
}

// ─── Card list renderer (v2 — uses BetCardV2) ────────────────────────────────

function CardList({ paginatedCards, paginatedOptions, isGrouped, cardSharedProps }) {
  const {
    selectedLegs, handleAddFetchedLeg, openDeepLink,
    userProfile, getFixtureLogosFromTicker, getTeamLogo,
  } = cardSharedProps;

  const myBooks = userProfile?.preferences?.myBooks || [];

  const handleDeepLink = (data, book) => {
    const url = data && typeof data === 'object' ? data._deepLink : data;
    openDeepLink(url, book || data?._selectedBook);
  };

  const makeLogoProps = (option) => ({
    homeLogoUrl: option?.homeLogoUrl || option?.raw?.home_logo
      || getFixtureLogosFromTicker?.(option?.raw?.fixture_id, option?.raw?.home_team, option?.raw?.away_team)?.homeLogo
      || getTeamLogo?.(option?.raw?.home_team || option?.raw?.home_name, option?.raw?.sport) || null,
    awayLogoUrl: option?.awayLogoUrl || option?.raw?.away_logo
      || getFixtureLogosFromTicker?.(option?.raw?.fixture_id, option?.raw?.home_team, option?.raw?.away_team)?.awayLogo
      || getTeamLogo?.(option?.raw?.away_team || option?.raw?.away_name, option?.raw?.sport) || null,
    teamLogoUrl: option?.teamLogoUrl || option?.raw?.team_logo
      || getTeamLogo?.(option?.raw?.team || option?.raw?.selection, option?.raw?.sport) || null,
  });

  if (isGrouped) {
    return (
      <>
        {paginatedCards.map(([key, val]) => {
          const isNonGroup = key.startsWith('_nongroup_');
          if (isNonGroup) {
            const option = val;
            return (
              <BetCardV2
                key={key}
                option={option}
                isSelected={!!selectedLegs?.find(l => l.id === option.id)}
                onSelect={(opt) => handleAddFetchedLeg(opt)}
                onOpenDeepLink={handleDeepLink}
                myBooks={myBooks}
                {...makeLogoProps(option)}
              />
            );
          } else {
            const group = val;
            if (!group || !Array.isArray(group.lines) || group.lines.length === 0) return null;
            const firstLine = group.lines[0];
            return (
              <BetCardV2
                key={key}
                option={firstLine}
                groupedLines={group.lines}
                isSelected={(group.lines || []).some(l => selectedLegs?.find(sl => sl.id === l.id))}
                onSelect={(line) => handleAddFetchedLeg(line)}
                onOpenDeepLink={handleDeepLink}
                myBooks={myBooks}
                homeLogoUrl={getFixtureLogosFromTicker?.(group.fixtureId, group.homeTeam, group.awayTeam)?.homeLogo || getTeamLogo?.(group.homeTeam) || null}
                awayLogoUrl={getFixtureLogosFromTicker?.(group.fixtureId, group.homeTeam, group.awayTeam)?.awayLogo || getTeamLogo?.(group.awayTeam) || null}
                teamLogoUrl={null}
              />
            );
          }
        })}
      </>
    );
  }

  return (
    <>
      {paginatedOptions.map((option) => (
        <BetCardV2
          key={option.id}
          option={option}
          isSelected={!!selectedLegs?.find(l => l.id === option.id)}
          onSelect={(opt) => handleAddFetchedLeg(opt)}
          onOpenDeepLink={handleDeepLink}
          myBooks={myBooks}
          {...makeLogoProps(option)}
        />
      ))}
    </>
  );
}

// ─── Main export ──────────────────────────────────────────────────────────────

export default function NewResultsView({
  // Filter state
  searchText, setSearchText,
  filterSportsbooks, setFilterSportsbooks,
  filterMarkets, setFilterMarkets,
  filterPlayers, setFilterPlayers,
  filterResultType, setFilterResultType,
  filterDeepLinksOnly, setFilterDeepLinksOnly,
  filterPositiveEvOnly, setFilterPositiveEvOnly,
  filterLineType, setFilterLineType,
  filterLegCount, setFilterLegCount,
  sortBy, setSortBy,
  // Available options for filter UI
  availableSportsbooks,
  availableMarkets,
  availablePlayers,
  availableLegCounts,
  _effectiveBooksSort,
  setBooksViewOverride,
  _allBooksCount,
  _myBooksCount,
  lineTypeCounts,
  // Results data
  paginatedCards,
  paginatedOptions,
  isGrouped,
  totalFilteredCount,
  totalCount,
  renderableCount,
  currentPage,
  totalPages,
  onPageChange,
  isLiveQuery,
  // Query context
  pendingQuery,
  queryFeedback,
  handleQueryFeedback,
  // Card shared props bundle
  cardSharedProps,
  // Bet slip actions
  selectedLegs,
  onSaveBetSlip,
  computeCap,
  // Show-all-math override toggle
  showAllMathOverride,
  onToggleShowAllMath,
  // Width for responsive layout
  screenWidth,
  // Optional production-only (lab omits these)
  preferredSportsbook,
  setPreferredSportsbook,
  isSgpResults,
  didBroaden,
  responseMetadata,
  isAdmin,
  debugPanelExpanded,
  setDebugPanelExpanded,
  canShowDevResultsCopy,
  handleCopyAllResults,
  copyResultsFlash,
}) {
  const [drawerOpen, setDrawerOpen] = useState(false);

  const width = screenWidth || SCREEN_WIDTH;
  const isDesktop = width >= 1024;
  const myBooks = cardSharedProps?.userProfile?.preferences?.myBooks || [];

  const hasActiveFilters = !!(
    searchText ||
    filterSportsbooks?.length > 0 ||
    filterMarkets?.length > 0 ||
    filterPlayers?.length > 0 ||
    filterResultType !== 'all' ||
    filterDeepLinksOnly ||
    filterPositiveEvOnly ||
    filterLineType !== 'all' ||
    filterLegCount != null ||
    sortBy !== 'ev'
  );

  const activeFilterCount = [
    filterSportsbooks?.length > 0,
    filterMarkets?.length > 0,
    filterPlayers?.length > 0,
    filterResultType !== 'all',
    filterDeepLinksOnly,
    filterPositiveEvOnly,
    filterLineType !== 'all',
    filterLegCount != null,
  ].filter(Boolean).length;

  const handleClearAll = useCallback(() => {
    setSearchText('');
    setFilterSportsbooks([]);
    setFilterMarkets([]);
    setFilterPlayers([]);
    setFilterResultType('all');
    setFilterDeepLinksOnly(false);
    setFilterPositiveEvOnly(false);
    setFilterLineType('all');
    setFilterLegCount(null);
    setSortBy('ev');
    if (typeof setBooksViewOverride === 'function') setBooksViewOverride(null);
    if (typeof setPreferredSportsbook === 'function') setPreferredSportsbook(null);
  }, [setSearchText, setFilterSportsbooks, setFilterMarkets, setFilterPlayers, setFilterResultType, setFilterDeepLinksOnly, setFilterPositiveEvOnly, setFilterLineType, setFilterLegCount, setSortBy, setBooksViewOverride, setPreferredSportsbook]);

  const emptySpec = describeResultsEmpty({
    fetchedCount: totalCount,
    filteredCount: totalFilteredCount,
    renderableCount: typeof renderableCount === 'number'
      ? renderableCount
      : (paginatedOptions?.length || 0) + (paginatedCards?.length || 0),
    filterDeepLinksOnly,
    filterPositiveEvOnly,
    filterLineType,
    isSgpResults,
    preferredSportsbook: preferredSportsbook || cardSharedProps?.preferredSportsbook,
    filterSportsbooks,
  });

  const onEmptyAction = useCallback(() => {
    if (emptySpec?.action === 'clearPlusEv') {
      setFilterPositiveEvOnly(false);
      return;
    }
    if (emptySpec?.action === 'clearEvAndDeepLinks') {
      setFilterPositiveEvOnly(false);
      setFilterDeepLinksOnly(false);
      return;
    }
    if (emptySpec?.action === 'clearDeepLinks') {
      setFilterDeepLinksOnly(false);
      return;
    }
    if (emptySpec?.action === 'clearLineType') {
      setFilterLineType('all');
      return;
    }
    if (emptySpec?.action === 'clearFocusBook') {
      setFilterSportsbooks([]);
      if (typeof setPreferredSportsbook === 'function') setPreferredSportsbook(null);
      return;
    }
    handleClearAll();
  }, [emptySpec, setFilterPositiveEvOnly, setFilterDeepLinksOnly, setFilterLineType, setFilterSportsbooks, setPreferredSportsbook, handleClearAll]);

  const filterProps = {
    searchText, setSearchText,
    filterSportsbooks, setFilterSportsbooks,
    filterMarkets, setFilterMarkets,
    filterPlayers, setFilterPlayers,
    filterResultType, setFilterResultType,
    filterDeepLinksOnly, setFilterDeepLinksOnly,
    filterPositiveEvOnly, setFilterPositiveEvOnly,
    filterLineType, setFilterLineType,
    filterLegCount, setFilterLegCount,
    sortBy, setSortBy,
    availableSportsbooks, availableMarkets, availablePlayers,
    myBooks, totalFilteredCount, totalCount, isLiveQuery,
    onClearAll: handleClearAll,
    hasActiveFilters,
    availableLegCounts,
    _effectiveBooksSort,
    setBooksViewOverride,
    _allBooksCount,
    _myBooksCount,
    lineTypeCounts,
  };

  // ─── Results header ─────────────────────────────────────────────────────────
  const resultsHeader = (
    <View style={styles.resultsHeader}>
      <View style={{ flexDirection: 'row', alignItems: 'center', gap: 10, flex: 1, flexWrap: 'wrap' }}>
        <Text style={styles.resultsCount}>
          <Text style={styles.resultsCountNum}>{totalFilteredCount}</Text>
          <Text style={styles.resultsCountDim}> of {totalCount} lines</Text>
        </Text>
        {didBroaden && (
          <View style={styles.broadenedPill}>
            <Text style={styles.broadenedText}>BROADENED</Text>
          </View>
        )}
        {isLiveQuery && (
          <View style={styles.livePill}>
            <View style={styles.liveDot} />
            <Text style={styles.liveText}>LIVE</Text>
          </View>
        )}
        {pendingQuery && (
          <Text style={styles.queryLabel} numberOfLines={1}>{pendingQuery}</Text>
        )}
      </View>
      <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
        {canShowDevResultsCopy && typeof handleCopyAllResults === 'function' && (
          <TouchableOpacity onPress={handleCopyAllResults} activeOpacity={0.8} style={styles.mathToggle}>
            <Ionicons name={copyResultsFlash ? 'checkmark' : 'copy-outline'} size={14} color={copyResultsFlash ? '#4ade80' : 'rgba(255,255,255,0.4)'} />
            <Text style={[styles.mathToggleText, copyResultsFlash && { color: '#4ade80' }]}>
              {copyResultsFlash ? 'Copied' : 'Copy'}
            </Text>
          </TouchableOpacity>
        )}
        {/* Show all math toggle */}
        <TouchableOpacity
          onPress={onToggleShowAllMath}
          activeOpacity={0.8}
          style={styles.mathToggle}
        >
          <Ionicons
            name={showAllMathOverride !== false ? 'calculator' : 'calculator-outline'}
            size={14}
            color={showAllMathOverride !== false ? '#A259FF' : 'rgba(255,255,255,0.4)'}
          />
          <Text style={[styles.mathToggleText, showAllMathOverride !== false && { color: '#A259FF' }]}>
            Math
          </Text>
        </TouchableOpacity>
        {/* Feedback */}
        <View style={{ flexDirection: 'row', gap: 4, alignItems: 'center' }}>
          <Text style={styles.feedbackLabel}>AI?</Text>
          <TouchableOpacity onPress={() => handleQueryFeedback?.('up')} style={styles.feedbackBtn} activeOpacity={0.7}>
            <Ionicons name="thumbs-up" size={14} color={queryFeedback === 'up' ? '#4ade80' : 'rgba(255,255,255,0.4)'} />
          </TouchableOpacity>
          <TouchableOpacity onPress={() => handleQueryFeedback?.('down')} style={styles.feedbackBtn} activeOpacity={0.7}>
            <Ionicons name="thumbs-down" size={14} color={queryFeedback === 'down' ? '#f87171' : 'rgba(255,255,255,0.4)'} />
          </TouchableOpacity>
        </View>
      </View>
    </View>
  );

  // ─── Card area ──────────────────────────────────────────────────────────────
  const cardArea = (
    <View style={styles.cardArea}>
      {resultsHeader}

      {isAdmin && responseMetadata ? (
        <PipelineDebugPanel
          responseMetadata={responseMetadata}
          expanded={!!debugPanelExpanded}
          onToggle={() => setDebugPanelExpanded?.((e) => !e)}
        />
      ) : null}

      {/* Bet slip header (when results exist) */}
      <View style={styles.betSlipRow} nativeID="suggested-legs-anchor">
        <Text style={styles.betSlipTitle}>Suggested Legs</Text>
        <TouchableOpacity onPress={onSaveBetSlip} style={styles.saveBetSlipBtn} activeOpacity={0.85}>
          <Ionicons name="bookmark-outline" size={13} color="rgba(255,255,255,0.7)" />
          <Text style={styles.saveBetSlipText}>Save Slip ({selectedLegs?.length || 0}/{computeCap?.() || 3})</Text>
        </TouchableOpacity>
      </View>

      {emptySpec ? (
        <View style={styles.emptyWrap}>
          <Ionicons
            name={emptySpec.kind === 'deeplinks' || emptySpec.kind === 'evAndDeepLinks' ? 'link-outline' : emptySpec.kind === 'plusEv' ? 'trending-up-outline' : emptySpec.kind === 'focusBook' ? 'ban-outline' : emptySpec.kind === 'ghost' ? 'albums-outline' : 'filter-outline'}
            size={28}
            color="rgba(255,255,255,0.25)"
          />
          <Text style={styles.emptyTitle}>{emptySpec.title}</Text>
          <Text style={styles.emptyBody}>{emptySpec.body}</Text>
          <TouchableOpacity onPress={onEmptyAction} style={styles.emptyBtn} activeOpacity={0.85}>
            <Text style={styles.emptyBtnText}>{emptySpec.actionLabel}</Text>
          </TouchableOpacity>
        </View>
      ) : (
        <>
          <PaginationRow currentPage={currentPage} totalPages={totalPages} onPageChange={onPageChange} />
          <View style={styles.cardList}>
            <CardList
              paginatedCards={paginatedCards}
              paginatedOptions={paginatedOptions}
              isGrouped={isGrouped}
              cardSharedProps={{ ...cardSharedProps, filterSportsbooks, _effectiveBooksSort: cardSharedProps?._effectiveBooksSort, sortBy }}
            />
          </View>
          <PaginationRow currentPage={currentPage} totalPages={totalPages} onPageChange={onPageChange} />
        </>
      )}
    </View>
  );

  // ─── Desktop layout: sidebar + main ─────────────────────────────────────────
  if (isDesktop) {
    return (
      <View style={styles.desktopRoot}>
        {/* Left sidebar */}
        <View style={styles.sidebar}>
          <ScrollView
            style={{ flex: 1 }}
            contentContainerStyle={{ paddingBottom: 40 }}
            showsVerticalScrollIndicator={false}
          >
            <FilterContent {...filterProps} />
          </ScrollView>
        </View>
        {/* Right main area */}
        <View style={styles.mainArea}>
          {cardArea}
        </View>
      </View>
    );
  }

  // ─── Mobile/tablet layout: filter drawer + cards ─────────────────────────────
  return (
    <View style={styles.mobileRoot}>
      {/* Top bar with filter button */}
      <View style={styles.mobileTopBar}>
        <TouchableOpacity
          onPress={() => setDrawerOpen(true)}
          activeOpacity={0.8}
          style={[styles.filterButton, hasActiveFilters && styles.filterButtonActive]}
        >
          <Ionicons name="options-outline" size={15} color={hasActiveFilters ? '#A259FF' : 'rgba(255,255,255,0.8)'} />
          <Text style={[styles.filterButtonText, hasActiveFilters && { color: '#A259FF' }]}>
            Filters{activeFilterCount > 0 ? ` (${activeFilterCount})` : ''}
          </Text>
        </TouchableOpacity>
        <View style={{ flexDirection: 'row', gap: 6 }}>
          {SORT_OPTIONS.slice(0, 3).map(opt => (
            <TouchableOpacity
              key={opt.value}
              onPress={() => setSortBy(opt.value)}
              activeOpacity={0.8}
              style={[styles.sortChip, sortBy === opt.value && styles.sortChipActive]}
            >
              <Text style={[styles.sortChipText, sortBy === opt.value && styles.sortChipTextActive]}>
                {opt.label}
              </Text>
            </TouchableOpacity>
          ))}
        </View>
      </View>

      {cardArea}

      {/* Filter drawer modal */}
      <Modal
        visible={drawerOpen}
        transparent
        animationType="slide"
        onRequestClose={() => setDrawerOpen(false)}
      >
        <View style={styles.drawerOverlay}>
          <TouchableOpacity
            style={StyleSheet.absoluteFill}
            onPress={() => setDrawerOpen(false)}
            activeOpacity={1}
          />
          <View style={styles.drawer}>
            <View style={styles.drawerHandle} />
            <View style={styles.drawerHeader}>
              <Text style={styles.drawerTitle}>Filters & Sort</Text>
              <TouchableOpacity onPress={() => setDrawerOpen(false)} style={styles.drawerClose} activeOpacity={0.8}>
                <Ionicons name="close" size={20} color="rgba(255,255,255,0.7)" />
              </TouchableOpacity>
            </View>
            <ScrollView style={{ flex: 1 }} contentContainerStyle={{ paddingBottom: 40 }} showsVerticalScrollIndicator={false}>
              <FilterContent {...filterProps} />
            </ScrollView>
            <TouchableOpacity
              onPress={() => setDrawerOpen(false)}
              style={styles.drawerApplyBtn}
              activeOpacity={0.85}
            >
              <Text style={styles.drawerApplyText}>
                Show {totalFilteredCount} result{totalFilteredCount !== 1 ? 's' : ''}
              </Text>
            </TouchableOpacity>
          </View>
        </View>
      </Modal>
    </View>
  );
}

// ─── Styles ───────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  desktopRoot: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    width: '100%',
  },
  sidebar: {
    width: 260,
    minWidth: 220,
    maxWidth: 280,
    borderRightWidth: 1,
    borderRightColor: 'rgba(255,255,255,0.07)',
    paddingRight: 0,
    // Sticky sidebar via position on web
    ...(Platform.OS === 'web' ? { position: 'sticky', top: 96, maxHeight: 'calc(100vh - 96px)' } : {}),
  },
  mainArea: {
    flex: 1,
    minWidth: 0,
    paddingLeft: 20,
  },
  mobileRoot: {
    flex: 1,
    width: '100%',
  },
  mobileTopBar: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    paddingHorizontal: 12,
    paddingVertical: 10,
    borderBottomWidth: 1,
    borderBottomColor: 'rgba(255,255,255,0.07)',
    flexWrap: 'wrap',
  },
  filterButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 5,
    paddingHorizontal: 12,
    paddingVertical: 7,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.15)',
    backgroundColor: 'rgba(255,255,255,0.04)',
  },
  filterButtonActive: {
    borderColor: 'rgba(162,89,255,0.5)',
    backgroundColor: 'rgba(162,89,255,0.08)',
  },
  filterButtonText: {
    color: 'rgba(255,255,255,0.8)',
    fontSize: 13,
    fontWeight: '600',
  },
  sortChip: {
    paddingHorizontal: 10,
    paddingVertical: 6,
    borderRadius: 7,
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.1)',
    backgroundColor: 'rgba(255,255,255,0.03)',
  },
  sortChipActive: {
    borderColor: 'rgba(162,89,255,0.5)',
    backgroundColor: 'rgba(162,89,255,0.12)',
  },
  sortChipText: {
    color: 'rgba(255,255,255,0.6)',
    fontSize: 12,
    fontWeight: '500',
  },
  sortChipTextActive: {
    color: '#c4b5fd',
    fontWeight: '700',
  },
  // Results area
  cardArea: {
    flex: 1,
    paddingTop: 4,
  },
  resultsHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 4,
    paddingVertical: 10,
    gap: 8,
  },
  resultsCountNum: {
    color: '#fff',
    fontSize: 15,
    fontWeight: '700',
  },
  resultsCountDim: {
    color: 'rgba(255,255,255,0.45)',
    fontSize: 13,
    fontWeight: '400',
  },
  livePill: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    backgroundColor: 'rgba(16,185,129,0.12)',
    borderRadius: 6,
    paddingHorizontal: 7,
    paddingVertical: 2,
    borderWidth: 1,
    borderColor: 'rgba(16,185,129,0.35)',
  },
  liveDot: {
    width: 5,
    height: 5,
    borderRadius: 3,
    backgroundColor: '#10B981',
  },
  liveText: {
    color: '#10B981',
    fontSize: 10,
    fontWeight: '700',
    letterSpacing: 0.5,
  },
  broadenedPill: {
    backgroundColor: 'rgba(251, 191, 36, 0.12)',
    borderRadius: 6,
    paddingHorizontal: 7,
    paddingVertical: 2,
    borderWidth: 1,
    borderColor: 'rgba(251, 191, 36, 0.35)',
  },
  broadenedText: {
    color: '#FBBF24',
    fontSize: 10,
    fontWeight: '700',
    letterSpacing: 0.5,
  },
  emptyWrap: {
    alignItems: 'center',
    paddingVertical: 36,
    paddingHorizontal: 24,
    gap: 10,
    marginTop: 12,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.08)',
    backgroundColor: 'rgba(255,255,255,0.03)',
  },
  emptyTitle: {
    color: '#fff',
    fontSize: 16,
    fontWeight: '700',
    textAlign: 'center',
  },
  emptyBody: {
    color: 'rgba(255,255,255,0.55)',
    fontSize: 13,
    lineHeight: 20,
    textAlign: 'center',
    maxWidth: 420,
  },
  emptyBtn: {
    marginTop: 8,
    paddingHorizontal: 16,
    paddingVertical: 10,
    borderRadius: 8,
    backgroundColor: 'rgba(162,89,255,0.18)',
    borderWidth: 1,
    borderColor: 'rgba(162,89,255,0.4)',
  },
  emptyBtnText: {
    color: '#c4b5fd',
    fontSize: 13,
    fontWeight: '600',
  },
  queryLabel: {
    color: 'rgba(255,255,255,0.35)',
    fontSize: 12,
    fontStyle: 'italic',
    maxWidth: 200,
  },
  mathToggle: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    paddingHorizontal: 8,
    paddingVertical: 5,
    borderRadius: 7,
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.1)',
    backgroundColor: 'rgba(255,255,255,0.03)',
  },
  mathToggleText: {
    fontSize: 12,
    color: 'rgba(255,255,255,0.4)',
    fontWeight: '500',
  },
  feedbackLabel: {
    color: 'rgba(255,255,255,0.4)',
    fontSize: 11,
  },
  feedbackBtn: {
    padding: 2,
  },
  betSlipRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 4,
    paddingBottom: 10,
    borderBottomWidth: 1,
    borderBottomColor: 'rgba(255,255,255,0.06)',
    marginBottom: 10,
  },
  betSlipTitle: {
    color: 'rgba(255,255,255,0.85)',
    fontSize: 14,
    fontWeight: '700',
    letterSpacing: 0.2,
  },
  saveBetSlipBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 5,
    paddingHorizontal: 10,
    paddingVertical: 6,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.15)',
    backgroundColor: 'rgba(255,255,255,0.05)',
  },
  saveBetSlipText: {
    color: 'rgba(255,255,255,0.7)',
    fontSize: 12,
    fontWeight: '600',
  },
  cardList: {
    gap: 0,
    marginTop: 4,
  },
  // Drawer
  drawerOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.6)',
    justifyContent: 'flex-end',
  },
  drawer: {
    backgroundColor: '#0f0f0f',
    borderTopLeftRadius: 20,
    borderTopRightRadius: 20,
    maxHeight: '85%',
    borderTopWidth: 1,
    borderTopColor: 'rgba(255,255,255,0.1)',
  },
  drawerHandle: {
    width: 36,
    height: 4,
    borderRadius: 2,
    backgroundColor: 'rgba(255,255,255,0.2)',
    alignSelf: 'center',
    marginTop: 10,
    marginBottom: 6,
  },
  drawerHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 20,
    paddingVertical: 12,
    borderBottomWidth: 1,
    borderBottomColor: 'rgba(255,255,255,0.07)',
  },
  drawerTitle: {
    color: '#fff',
    fontSize: 16,
    fontWeight: '700',
  },
  drawerClose: {
    padding: 4,
  },
  drawerApplyBtn: {
    margin: 16,
    paddingVertical: 14,
    borderRadius: 12,
    backgroundColor: '#A259FF',
    alignItems: 'center',
  },
  drawerApplyText: {
    color: '#fff',
    fontSize: 15,
    fontWeight: '700',
  },
});

// Filter content styles
const filterContentStyles = StyleSheet.create({
  root: {
    padding: 12,
    gap: 4,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 10,
  },
  headerTitle: {
    color: 'rgba(255,255,255,0.85)',
    fontSize: 13,
    fontWeight: '700',
    letterSpacing: 0.3,
    textTransform: 'uppercase',
  },
  activeBadge: {
    backgroundColor: 'rgba(162,89,255,0.25)',
    borderRadius: 4,
    paddingHorizontal: 5,
    paddingVertical: 1,
  },
  activeBadgeText: {
    color: '#c4b5fd',
    fontSize: 9,
    fontWeight: '800',
    letterSpacing: 0.5,
  },
  countText: {
    color: '#fff',
    fontSize: 12,
    fontWeight: '600',
  },
  livePill: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 3,
    backgroundColor: 'rgba(16,185,129,0.12)',
    borderRadius: 5,
    paddingHorizontal: 5,
    paddingVertical: 2,
    borderWidth: 1,
    borderColor: 'rgba(16,185,129,0.35)',
  },
  liveDot: {
    width: 4,
    height: 4,
    borderRadius: 2,
    backgroundColor: '#10B981',
  },
  liveText: {
    color: '#10B981',
    fontSize: 9,
    fontWeight: '700',
    letterSpacing: 0.4,
  },
  searchRow: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: 'rgba(255,255,255,0.05)',
    borderRadius: 8,
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.1)',
    paddingHorizontal: 10,
    paddingVertical: 7,
    marginBottom: 6,
  },
  searchInput: {
    flex: 1,
    color: '#fff',
    fontSize: 13,
    ...(Platform.OS === 'web' ? { outlineStyle: 'none' } : {}),
  },
  clearBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 5,
    paddingVertical: 4,
    marginBottom: 4,
  },
  clearBtnText: {
    color: '#A259FF',
    fontSize: 12,
    fontWeight: '600',
  },
  sortRow: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 6,
    paddingHorizontal: 8,
    borderRadius: 6,
  },
  sortRowActive: {
    backgroundColor: 'rgba(162,89,255,0.1)',
  },
  sortLabel: {
    color: 'rgba(255,255,255,0.6)',
    fontSize: 13,
  },
  sortLabelActive: {
    color: '#c4b5fd',
    fontWeight: '600',
  },
  toggleRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  toggleTrack: {
    width: 32,
    height: 18,
    borderRadius: 9,
    backgroundColor: 'rgba(255,255,255,0.1)',
    justifyContent: 'center',
    padding: 2,
  },
  toggleTrackOn: {
    backgroundColor: '#A259FF',
  },
  toggleThumb: {
    width: 14,
    height: 14,
    borderRadius: 7,
    backgroundColor: 'rgba(255,255,255,0.5)',
    alignSelf: 'flex-start',
  },
  toggleThumbOn: {
    backgroundColor: '#fff',
    alignSelf: 'flex-end',
  },
  toggleLabel: {
    color: 'rgba(255,255,255,0.7)',
    fontSize: 13,
  },
});

// Chip styles
const chipStyles = StyleSheet.create({
  chip: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 9,
    paddingVertical: 5,
    borderRadius: 6,
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.12)',
    backgroundColor: 'rgba(255,255,255,0.04)',
  },
  chipActive: {
    borderColor: 'rgba(162,89,255,0.6)',
    backgroundColor: 'rgba(162,89,255,0.12)',
  },
  chipText: {
    color: 'rgba(255,255,255,0.65)',
    fontSize: 12,
    fontWeight: '500',
  },
  chipTextActive: {
    color: '#c4b5fd',
    fontWeight: '700',
  },
});

// Accordion styles
const accordionStyles = StyleSheet.create({
  section: {
    borderTopWidth: 1,
    borderTopColor: 'rgba(255,255,255,0.05)',
    paddingTop: 2,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingVertical: 9,
    paddingHorizontal: 4,
  },
  headerText: {
    color: 'rgba(255,255,255,0.6)',
    fontSize: 11,
    fontWeight: '700',
    textTransform: 'uppercase',
    letterSpacing: 0.5,
  },
  badge: {
    backgroundColor: 'rgba(162,89,255,0.3)',
    borderRadius: 8,
    minWidth: 16,
    height: 16,
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: 4,
  },
  badgeText: {
    color: '#c4b5fd',
    fontSize: 10,
    fontWeight: '700',
  },
  body: {
    paddingBottom: 10,
    paddingHorizontal: 2,
  },
});

// Pagination styles
const pgStyles = StyleSheet.create({
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 4,
    paddingVertical: 12,
    flexWrap: 'wrap',
  },
  btn: {
    width: 32,
    height: 32,
    borderRadius: 8,
    alignItems: 'center',
    justifyContent: 'center',
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.1)',
    backgroundColor: 'rgba(255,255,255,0.04)',
  },
  btnActive: {
    backgroundColor: 'rgba(162,89,255,0.25)',
    borderColor: 'rgba(162,89,255,0.6)',
  },
  btnDisabled: {
    opacity: 0.3,
  },
  btnText: {
    color: 'rgba(255,255,255,0.7)',
    fontSize: 13,
    fontWeight: '500',
  },
  btnTextActive: {
    color: '#c4b5fd',
    fontWeight: '700',
  },
  ellipsis: {
    color: 'rgba(255,255,255,0.3)',
    fontSize: 13,
    paddingHorizontal: 2,
  },
});
